import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { RootState } from '../redux/store';
import { createPatient } from '../services/api';
import { PatientDetails as PatientDetailsType, setPatientDetails, setPatientRequestId } from '../redux/slices/appointmentSlice';
import { StepCard } from '../components/StepCard';
import { Button } from '../components/Button';
import { User, Mail, NotebookPen, Phone } from 'lucide-react';
import { PatientTypeEnum, PATIENT_TYPE_TO_BACKEND } from '../types';

const PHONE_REGEX = /^\(\d{3}\)\d{3}-\d{4}$/;

export const StepPatientDetails = () => {
  const {patientType, patientDetails} = useSelector((s: RootState) => s.appointment);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const update = (field: keyof PatientDetailsType, value: string) => {
    dispatch(setPatientDetails({ ...patientDetails, [field]: value }));
  };

  const handlePhoneChange = (nextValue: string) => {
    const digits = nextValue.replace(/\D/g, '').slice(0, 10);

    if (!digits) {
      update('phone', '');
      return;
    }

    if (digits.length < 4) {
      update('phone', `(${digits}`);
      return;
    }

    if (digits.length < 7) {
      update('phone', `(${digits.slice(0, 3)})${digits.slice(3)}`);
      return;
    }

    update('phone', `(${digits.slice(0, 3)})${digits.slice(3, 6)}-${digits.slice(6)}`);
  };
  const proceed = () => {
    navigate(`/bookonline/confirmation`);
  };

  const canContinue = Boolean(
    patientDetails.firstName &&
    patientDetails.lastName &&
    PHONE_REGEX.test(patientDetails.phone) &&
    patientDetails.email
  );

  const savePatient = async () => {
    if (!PHONE_REGEX.test(patientDetails.phone)) {
      return;
    }
    try {
    const payload = {
      first_name: patientDetails.firstName,
      last_name: patientDetails.lastName,
      email: patientDetails.email,
      phone_number: patientDetails.phone,
      patient_type: PATIENT_TYPE_TO_BACKEND[patientType as PatientTypeEnum],
    };

      const patient_request = await createPatient(payload);
      dispatch(setPatientRequestId(patient_request.request_id));
      proceed();
    } catch (error) {
      console.error('Error creating patient:', error);
    }
};

  return (
    <Layout>
      <div className="grid grid-cols-1 md:grid-cols-1">
        <aside className="hidden md:block" />
        <section className="md:col-span-2 space-y-4">
          <div>
            <h1 className="text-2xl font-semibold text-black">Patient details</h1>
            <p className="text-sm text-gray-500">Provide your contact information for appointment confirmation.</p>
          </div>
          <StepCard className='overflow-y-auto'>  
            <div className="space-y-4">
              {/* Required Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">First Name <span className="text-error">*</span></span>
                  </label>
                  <label className="input input-bordered border-teamcare-blue flex items-center gap-2 bg-white text-black">
                    <User size={18} className="text-gray-400" />
                    <input
                      type="text"
                      className="grow placeholder-gray-400"
                      placeholder="John"
                      value={patientDetails.firstName}
                      onChange={(e) => update('firstName', e.target.value)}
                    />
                  </label>
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Last Name <span className="text-error">*</span></span>
                  </label>
                  <label className="input input-bordered flex border-teamcare-blue items-center gap-2 bg-white text-black">
                    <User size={18} className="text-gray-400" />
                    <input
                      type="text"
                      className="grow bg-transparent text-black placeholder-gray-400"
                      placeholder="Doe"
                      value={patientDetails.lastName}
                      onChange={(e) => update('lastName', e.target.value)}
                    />
                  </label>
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Phone Number <span className="text-error">*</span></span>
                  </label>
                  <label className="input input-bordered border-teamcare-blue flex items-center gap-2 bg-white text-black">
                    <Phone size={18} className="text-gray-400" />
                    <input
                      type="text"
                      className="grow bg-transparent text-black placeholder-gray-400"
                      placeholder="(XXX)XXX-XXXX"
                      value={patientDetails.phone}
                      onChange={(e) => handlePhoneChange(e.target.value)}
                      maxLength={13}
                      inputMode="numeric"
                    />
                  </label>
                </div>
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Email <span className="text-error">*</span></span>
                  </label>
                  <label className="input input-bordered flex border-teamcare-blue items-center gap-2 bg-white text-black">
                    <Mail size={18} className="text-gray-400" />
                    <input
                      type="email"
                      className="grow bg-transparent text-black placeholder-gray-400"
                      placeholder="john.doe@example.com"
                      value={patientDetails.email}
                      onChange={(e) => update('email', e.target.value)}
                    />
                  </label>
                </div>

                <div className="form-control md:col-span-2">
                  <label className="label">
                    <span className="label-text">Appointment Notes</span>
                  </label>
                  <div className="relative">
                    <div className="pointer-events-none absolute left-3 top-3">
                      <NotebookPen size={18} className="text-gray-400" />
                    </div>
                    <textarea
                      className="textarea textarea-bordered w-full pl-10 bg-transparent text-black placeholder-gray-400 border-teamcare-blue"
                      rows={4}
                      placeholder="Add any notes for your appointment (optional)"
                      value={patientDetails.notes || ''}
                      onChange={(e) => update('notes', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>
          </StepCard>
          <div className="flex justify-between">
            <Button title='Back' variant='secondary' onClick={() => navigate('/bookonline/step-3')}/>
            <Button title='Continue' disabled={!canContinue} onClick={savePatient}/>
          </div>
        </section>
      </div>
    </Layout>
  );
};